/**
 * Figma's Space: while the key is held, a drag anywhere on the plan or in the scene pans the
 * view, whatever lies under the pointer — the selected sofa included.
 */
let held = false;
/** Whether the pointer was last seen over the plan or the scene canvas. */
let overCanvas = false;
/** When the last press and the last key came, and whether the element holding the focus got it from that press. */
let lastPress = -Infinity;
let lastKey = -Infinity;
let focusFromPointer = false;
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta']);
const listeners = new Set<(held: boolean) => void>();
const emit = () => { for (const listener of listeners) listener(held); };
const isSpace = (event: KeyboardEvent) => event.code === 'Space' || event.key === ' ' || event.key === 'Spacebar';
const element = (target: EventTarget | null) => target instanceof Element ? target : null;
const textFocused = (target: EventTarget | null) => !!element(target)?.closest('input, textarea, select, [contenteditable=true]');
const controlFocused = (target: EventTarget | null) => !!element(target)?.closest('button, a, summary, [role=button], [role=menuitem], [role=option], [role=tab]');
/** An open menu, list or dialog owns its keys, whoever focused it. */
const surfaceFocused = (target: EventTarget | null) => !!element(target)?.closest('[role=menu], [role=listbox], [role=dialog], dialog');

/**
 * Space pans unless the key belongs to something else: a text field; an open menu or dialog; a
 * control reached from the keyboard (Tab, a script) — that one presses with Space as usual. A
 * control that only holds the focus because it was clicked gives Space to the drawing while the
 * pointer is over it, as Figma does. `:focus-visible` cannot tell the two apart: Chrome turns it
 * on for the focused element as soon as any key goes down.
 */
function down(event: KeyboardEvent): void {
  if (!isSpace(event) && !MODIFIERS.has(event.key)) lastKey = event.timeStamp;
  if (!isSpace(event) || event.defaultPrevented || textFocused(event.target) || surfaceFocused(event.target)) return;
  if (controlFocused(event.target) && !(overCanvas && focusFromPointer)) return;
  event.preventDefault();
  if (held || event.repeat) return;
  held = true; emit();
}
function up(event: KeyboardEvent): void {
  if (!isSpace(event) || !held) return;
  held = false; emit();
}
/** The window lost the key-up: a switched tab, a dragged-out pointer. */
function release(): void {
  if (!held) return;
  held = false; emit();
}
function track(event: PointerEvent): void {
  overCanvas = !!element(event.target)?.closest('canvas.plan-canvas, canvas.scene-canvas');
  if (event.type === 'pointerdown') lastPress = event.timeStamp;
}
function focused(event: FocusEvent): void {
  // A press focuses its target within a frame or two, and a dropdown hands the focus back a little later;
  // a key since that press (Tab, an arrow) means the keyboard moved the focus.
  focusFromPointer = event.timeStamp - lastPress < 800 && lastKey < lastPress;
}
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', down);
  window.addEventListener('keyup', up);
  window.addEventListener('blur', release);
  window.addEventListener('pointermove', track, { capture: true, passive: true });
  window.addEventListener('pointerdown', track, { capture: true, passive: true });
  window.addEventListener('focusin', focused, { capture: true });
}

export const spaceHeld = (): boolean => held;
/**
 * Whether Space now belongs to the drawing although a control holds the focus — the press gave it the focus and the
 * pointer is over the plan or the scene. A menu's button hears Space before the window does and asks first.
 */
export const spaceForDrawing = (): boolean => overCanvas && focusFromPointer;
/** Called with the new state whenever Space goes down or up; returns the unsubscribe. */
export function onSpace(listener: (held: boolean) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
/** For tests and probes: set the state as a key press would. */
export function setSpaceHeld(value: boolean): void {
  if (held === value) return;
  held = value; emit();
}
